/**
 * Gestión de Inscripciones - VERSIÓN SIMPLIFICADA PARA DEBUGGING
 */

// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Badge, Alert } from 'react-bootstrap';
import { FaArrowLeft, FaUser, FaCheckCircle } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';
import { useGTMContext } from '../../context/GTMContext';
import futbolistaService from '../../services/futbolistaService';
import productService from '../../services/productService';
import paymentService from '../../services/paymentService';
import userService from '../../services/userService';
import './InscripcionManagement.css';

const InscripcionManagementSimple = () => {
  // eslint-disable-next-line no-console
  console.log('🚀 DEBUGGING: InscripcionManagementSimple iniciando render');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const _torneoIdParam = searchParams.get('torneoId') || '';
  const torneoNombreParam = searchParams.get('torneoNombre') || '';
  const gtm = useGTMContext();

  // Estados básicos - PASO 3: Añadir paquetes
  const [searchTerm, setSearchTerm] = useState('');
  const [futbolistas, setFutbolistas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paquetes, setPaquetes] = useState([]);
  const [paquetesLoading, setPaquetesLoading] = useState(false);
  // PASO 4: Estados de selección
  const [selectedFutbolista, setSelectedFutbolista] = useState(null);
  const [selectedPaquete, setSelectedPaquete] = useState(null);
  const [processingInscription, setProcessingInscription] = useState(false);
  const [tutorInfo, setTutorInfo] = useState(null);

  // Colores para los paquetes (igual que el original)
  const paqueteColors = [
    { bg: 'bg-primary', text: 'text-white', border: 'border-primary' },
    { bg: 'bg-success', text: 'text-white', border: 'border-success' },
    { bg: 'bg-warning', text: 'text-dark', border: 'border-warning' },
    { bg: 'bg-info', text: 'text-white', border: 'border-info' },
    { bg: 'bg-danger', text: 'text-white', border: 'border-danger' },
    { bg: 'bg-secondary', text: 'text-white', border: 'border-secondary' }
  ];

  // eslint-disable-next-line no-console
  console.log('🚀 DEBUGGING: Estados inicializados correctamente');

  // PASO 3: Función para cargar paquetes
  const loadPaquetes = async () => {
    try {
      setPaquetesLoading(true);
      // eslint-disable-next-line no-console
      console.log('📦 PASO 3 - Cargando paquetes...');

      const response = await productService.getAvailableProducts();
      // eslint-disable-next-line no-console
      console.log('📦 PASO 3 - Respuesta paquetes:', response);

      if (response && response.success && response.data) {
        let paquetesActivos = response.data.filter(product =>
          product.active && product.paqueteId
        );
        // Filtrar por torneo si viene por query param
        if (torneoNombreParam) {
          paquetesActivos = paquetesActivos.filter(p =>
            p.torneo && p.torneo.nombre === torneoNombreParam
          );
        }
        setPaquetes(paquetesActivos);
        // eslint-disable-next-line no-console
        console.log('📦 PASO 3 - Paquetes cargados:', paquetesActivos.length);
        toast.success(`✅ ${paquetesActivos.length} paquetes cargados${torneoNombreParam ? ` (${torneoNombreParam})` : ''}`);
      } else {
        setPaquetes([]);
        toast.warning('⚠️ No se encontraron paquetes');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ PASO 3 - Error cargando paquetes:', error);
      toast.error(`Error cargando paquetes: ${error.message}`);
      setPaquetes([]);
    } finally {
      setPaquetesLoading(false);
    }
  };

  // PASO 3: useEffect para cargar paquetes automáticamente
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('🚀 PASO 3 - useEffect ejecutándose...');
    loadPaquetes();
  }, []);

  // PASO 4: Funciones de selección
  const handleSelectFutbolista = async (futbolista) => {
    // eslint-disable-next-line no-console
    console.log('👤 PASO 4 - Futbolista seleccionado:', futbolista);

    // Cargar información completa del futbolista (con inscripciones y saldo)
    try {
      const futbolistaCompleto = await futbolistaService.getFutbolistaById(futbolista.id);
      // eslint-disable-next-line no-console
      console.log('👤 PASO 4 - Futbolista completo:', futbolistaCompleto);

      setSelectedFutbolista(futbolistaCompleto);
      toast.info(`Futbolista seleccionado: ${futbolistaCompleto.nombre}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error cargando futbolista completo:', error);
      setSelectedFutbolista(futbolista);
      toast.info(`Futbolista seleccionado: ${futbolista.nombre}`);
    }

    // Cargar información del tutor
    if (futbolista.creado_por_mongodb_id) {
      try {
        // eslint-disable-next-line no-console
        console.log('👤 PASO 4 - Cargando tutor:', futbolista.creado_por_mongodb_id);
        const tutor = await userService.getUserById(futbolista.creado_por_mongodb_id);
        setTutorInfo(tutor);
        // eslint-disable-next-line no-console
        console.log('👤 PASO 4 - Tutor cargado:', tutor);
        toast.success(`Tutor: ${tutor.name}`);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Error cargando tutor:', error);
        toast.error('Error al cargar información del tutor');
        setTutorInfo(null);
      }
    }
  };

  const handleSelectPaquete = (paquete) => {
    // eslint-disable-next-line no-console
    console.log('📦 PASO 4 - Paquete seleccionado:', paquete);
    setSelectedPaquete(paquete);
    toast.info(`Paquete seleccionado: ${paquete.name}`);
  };

  // PASO 4: Función de inscripción REAL (igual que el componente original)
  const handleInscripcion = async () => {
    if (!selectedFutbolista || !selectedPaquete || !tutorInfo) {
      toast.error('Faltan datos para procesar la inscripción');
      return;
    }

    // Confirmación con alert nativo
    const confirmMessage = `¿Confirmar inscripción?\n\nFutbolista: ${selectedFutbolista.nombre}\nTutor: ${tutorInfo.name}\nPaquete: ${selectedPaquete.name} - ${selectedPaquete.price}€\nJornadas: ${selectedPaquete.sessions}\n\nMétodo: Pago en efectivo`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setProcessingInscription(true);
      // eslint-disable-next-line no-console
      console.log('🚀 PASO 4 - Procesando inscripción REAL...');

      // Datos para el endpoint de inscripción (igual que transferencia)
      const inscripcionData = {
        paquete_id: selectedPaquete.paqueteId, // ID del paquete en PostgreSQL
        futbolista_id: selectedFutbolista.id,
        user_id: tutorInfo._id,
        equipacion: { incluir: false },
        precio_total: selectedPaquete.price,
        metodo_pago: 'cash', // Pago en efectivo
        pendiente_confirmacion: false, // Ya confirmado por admin
        product_id: selectedPaquete._id // ID del producto en MongoDB
      };

      // eslint-disable-next-line no-console
      console.log('📤 PASO 4 - Enviando datos:', inscripcionData);

      // Usar el servicio de pagos para inscripción directa
      const result = await paymentService.processDirectInscription(inscripcionData);
      // eslint-disable-next-line no-console
      console.log('✅ PASO 4 - Inscripción procesada:', result);

      // Enviar evento GTM de compra (inscripción en efectivo)
      gtm.sendPurchaseEvent(
        inscripcionData.transaction_id,
        selectedPaquete.price,
        'EUR',
        [{
          item_id: selectedPaquete._id,
          item_name: selectedPaquete.name,
          category: 'Paquete Jornadas',
          quantity: 1,
          price: selectedPaquete.price
        }]
      );

      // También enviar evento específico de inscripción admin
      gtm.sendCustomEvent('admin_cash_inscription', {
        futbolista_id: selectedFutbolista.id,
        futbolista_name: selectedFutbolista.nombre,
        tutor_id: tutorInfo._id,
        tutor_name: tutorInfo.name,
        paquete_id: selectedPaquete._id,
        paquete_name: selectedPaquete.name,
        sessions: selectedPaquete.sessions,
        value: selectedPaquete.price,
        currency: 'EUR',
        payment_method: 'cash',
        transaction_id: inscripcionData.transaction_id
      });

      // Mostrar éxito
      toast.success(`✅ Inscripción procesada para ${selectedFutbolista.nombre}`);
      toast.info(`💰 Saldo añadido: ${selectedPaquete.sessions} jornadas`);
      toast.info(`👤 Tutor: ${tutorInfo.name}`);

      // Limpiar selección
      setSelectedFutbolista(null);
      setTutorInfo(null);
      setSelectedPaquete(null);

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ PASO 4 - Error en inscripción:', error);
      toast.error(error.message || 'Error al procesar inscripción');
    } finally {
      setProcessingInscription(false);
    }
  };

  // Función de búsqueda - PASO 2: Añadir llamada real a API con debugging
  const handleSearch = async (term) => {
    // eslint-disable-next-line no-console
    console.log('🔍 PASO 2 - Búsqueda:', term);
    setSearchTerm(term);

    // Limpiar si es muy corto (mínimo 3 caracteres)
    if (!term || term.trim().length < 3) {
      setFutbolistas([]);
      return;
    }

    try {
      setLoading(true);
      // eslint-disable-next-line no-console
      console.log('📡 PASO 2 - Llamando a futbolistaService...');
      // eslint-disable-next-line no-console
      console.log('📡 PASO 2 - URL que se va a llamar:', `/api/postgres/futbolistas?search=${encodeURIComponent(term.trim())}`);

      const data = await futbolistaService.getFutbolistasBySearch(term.trim());

      // eslint-disable-next-line no-console
      console.log('📡 PASO 2 - Respuesta recibida:', data);
      // eslint-disable-next-line no-console
      console.log('📡 PASO 2 - Tipo de respuesta:', typeof data);
      // eslint-disable-next-line no-console
      console.log('📡 PASO 2 - Es array?:', Array.isArray(data));
      // eslint-disable-next-line no-console
      console.log('📡 PASO 2 - Longitud:', data?.length);

      const futbolistasArray = Array.isArray(data) ? data.slice(0, 50) : [];
      setFutbolistas(futbolistasArray);

      if (futbolistasArray.length > 0) {
        toast.success(`✅ Encontrados ${futbolistasArray.length} futbolistas`);
      } else {
        toast.warning(`⚠️ No se encontraron futbolistas para "${term}"`);
      }

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ PASO 2 - Error en búsqueda:', error);
      // eslint-disable-next-line no-console
      console.error('❌ PASO 2 - Error completo:', error.response || error);
      toast.error(`Error: ${error.message}`);
      setFutbolistas([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout
      title={`Gestión de Inscripciones${torneoNombreParam ? ` — ${torneoNombreParam}` : ''}`}
      subtitle="Inscribir futbolistas con pago en efectivo"
    >
      <Container fluid className="inscripcion-management inscripcion-white-bg p-0">
        {/* Botón de navegación */}
        <Row className="mb-3">
          <Col>
            <Button variant="outline-secondary" onClick={() => navigate('/admin/matches')}>
              <FaArrowLeft className="me-2" />Volver a Torneos
            </Button>
          </Col>
        </Row>

        {/* Búsqueda de futbolistas */}
        <Row className="mb-4">
          <Col xs={12}>
            <div>
              <Form.Control
                type="text"
                placeholder="🔍 Escribe al menos 3 caracteres para buscar..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                disabled={loading}
              />
              <small className="text-muted">
                💡 Búsqueda en tiempo real - Máximo 50 resultados
              </small>
            </div>
          </Col>
        </Row>

        {/* Lista de futbolistas */}
        {futbolistas.length > 0 && (
          <Row className="mb-4">
            <Col xs={12}>
              <h6 className="mb-3">👥 Futbolistas encontrados ({futbolistas.length})</h6>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {futbolistas.map((futbolista) => (
                  <Card
                    key={futbolista.id}
                    className={`mb-2 selection-card ${selectedFutbolista?.id === futbolista.id ? 'selected' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSelectFutbolista(futbolista)}
                  >
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{futbolista.nombre}</strong>
                          <br />
                          <small className="text-muted">
                            Nacimiento: {futbolista.fecha_nacimiento} | ID: {futbolista.id}
                          </small>
                        </div>
                        <div>
                          {selectedFutbolista?.id === futbolista.id ? (
                            <FaCheckCircle className="text-success" size={20} />
                          ) : (
                            <Badge bg="primary">Seleccionar</Badge>
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </Col>
          </Row>
        )}

        {/* PASO 3: Estado de paquetes */}
        {paquetesLoading && (
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Body className="text-center">
                  <Spinner size="sm" className="me-2" />
                  <span>Cargando paquetes disponibles...</span>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Estado de paquetes */}
        {paquetesLoading && (
          <Row className="mb-3">
            <Col>
              <Alert variant="info">
                <Spinner size="sm" className="me-2" />
                Cargando paquetes disponibles...
              </Alert>
            </Col>
          </Row>
        )}

        {paquetes.length > 0 && !paquetesLoading && (
          <Row className="mb-3">
            <Col>
              <Alert variant="success">
                ✅ {paquetes.length} paquetes cargados correctamente
              </Alert>
            </Col>
          </Row>
        )}

        {/* Lista de Paquetes */}
        {paquetes.length > 0 && (
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Paquetes Disponibles ({paquetes.length})</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    {paquetes.map((paquete, index) => {
                      const _colorScheme = paqueteColors[index % paqueteColors.length];
                      return (
                        <Col md={4} key={paquete._id} className="mb-3">
                          <div
                            className={`selection-card ${selectedPaquete?._id === paquete._id ? 'selected' : ''}`}
                            onClick={() => handleSelectPaquete(paquete)}
                            style={{
                              background: selectedPaquete?._id === paquete._id
                                ? `linear-gradient(135deg, var(--bs-success), var(--bs-success-dark))`
                                : `linear-gradient(135deg, var(--bs-light), var(--bs-white))`,
                              border: `2px solid ${selectedPaquete?._id === paquete._id ? 'var(--bs-success)' : 'var(--bs-border-color)'}`,
                              color: selectedPaquete?._id === paquete._id ? 'white' : 'var(--bs-body-color)'
                            }}
                          >
                            <div className="text-center">
                              <h6 className="fw-bold">{paquete.name}</h6>
                              <div className="h4 fw-bold">{paquete.price}€</div>
                              <p className="mb-2">{paquete.sessions} jornadas</p>

                              {/* Información de la sede */}
                              {paquete.sede && (
                                <div className="mb-2">
                                  <Badge bg="info" className="me-1">
                                    📍 {paquete.sede.nombre}
                                  </Badge>
                                  {paquete.sede.poblacion && (
                                    <div><small className="text-muted">{paquete.sede.poblacion}</small></div>
                                  )}
                                </div>
                              )}

                              {/* Información del torneo */}
                              {paquete.torneo && (
                                <div className="mb-2">
                                  <Badge bg="warning" className="text-dark">🏆 {paquete.torneo.nombre}</Badge>
                                </div>
                              )}

                              <small className="text-muted">{paquete.description}</small>

                              {paquete.code && (
                                <div className="mt-1"><Badge bg="secondary">{paquete.code}</Badge></div>
                              )}

                              {paquete.paqueteId && (
                                <div className="mt-1">
                                  <small className="text-muted">ID Paquete: {paquete.paqueteId}</small>
                                </div>
                              )}

                              {selectedPaquete?._id === paquete._id && (
                                <Badge bg="success" className="mt-2">✓ Seleccionado</Badge>
                              )}
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Información del Tutor y Futbolista Seleccionado */}
        {selectedFutbolista && (
          <Row className="mb-4">
            <Col md={6}>
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">Futbolista Seleccionado</h6>
                </Card.Header>
                <Card.Body>
                  <p><strong>Nombre:</strong> {selectedFutbolista.nombre}</p>
                  <p><strong>ID:</strong> {selectedFutbolista.id}</p>
                  <p><strong>Fecha Nacimiento:</strong> {selectedFutbolista.fecha_nacimiento}</p>

                  {/* Saldo de Jornadas del Futbolista */}
                  {selectedFutbolista.inscripciones && selectedFutbolista.inscripciones.length > 0 && (
                    <div className="mt-3 p-3 bg-success bg-opacity-10 rounded border border-success">
                      <h6 className="text-success mb-2">⚽ Saldo de Jornadas</h6>
                      {selectedFutbolista.inscripciones.map((inscripcion, idx) => (
                        <div key={idx} className="mb-2">
                          <p className="mb-1">
                            <strong>Torneo:</strong> {inscripcion.torneo?.nombre || 'N/A'}
                          </p>
                          <p className="mb-0">
                            <strong>Saldo Disponible:</strong>{' '}
                            <span className="text-success fw-bold fs-5">
                              {inscripcion.saldo_jornadas || 0} jornadas
                            </span>
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedFutbolista.inscripciones && selectedFutbolista.inscripciones.length === 0 && (
                    <div className="mt-3 p-3 bg-warning bg-opacity-10 rounded border border-warning">
                      <p className="text-warning mb-0">
                        ⚠️ Este futbolista no tiene inscripciones activas
                      </p>
                    </div>
                  )}

                  {!selectedFutbolista.inscripciones && (
                    <div className="mt-3 p-3 bg-secondary bg-opacity-10 rounded border border-secondary">
                      <p className="text-muted mb-0">
                        ℹ️ Cargando información de inscripciones...
                      </p>
                    </div>
                  )}

                  {/* Información del Tutor */}
                  {tutorInfo ? (
                    <div className="mt-3 p-3 bg-light rounded">
                      <h6><FaUser className="me-2" />Información del Tutor</h6>
                      <p><strong>Nombre:</strong> {tutorInfo.name}</p>
                      <p><strong>Email:</strong> {tutorInfo.email}</p>
                      <p><strong>Teléfono:</strong> {tutorInfo.phone || 'No disponible'}</p>
                      <p className="mb-0"><strong>Saldo Monedero:</strong> {tutorInfo.walletBalance || 0}€</p>
                    </div>
                  ) : (
                    <div className="mt-3 p-3 bg-warning bg-opacity-10 rounded">
                      <p className="text-warning mb-0">Cargando información del tutor...</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Botón de Procesar */}
        {selectedFutbolista && selectedPaquete && tutorInfo && (
          <Row className="mb-4">
            <Col>
              <Card className="border-success">
                <Card.Body className="text-center">
                  <h5>¿Procesar Inscripción?</h5>
                  <p>
                    <strong>Futbolista:</strong> {selectedFutbolista.nombre}<br />
                    <strong>Tutor:</strong> {tutorInfo.name}<br />
                    <strong>Paquete:</strong> {selectedPaquete.name} - {selectedPaquete.price}€<br />
                    <strong>Jornadas:</strong> {selectedPaquete.sessions}
                  </p>
                  <Button
                    variant="success"
                    size="lg"
                    onClick={handleInscripcion}
                    disabled={processingInscription}
                  >
                    {processingInscription ? <Spinner size="sm" /> : <FaCheckCircle />} Procesar Inscripción
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </AdminLayout>
  );
};

export default InscripcionManagementSimple;
